import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentOrgId } from "@/hooks/useSkillOS";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Breadcrumb } from "@/components/layout/Breadcrumb";
import { ShadowModeBanner } from "@/components/admin/ShadowModeBanner";
import { Brain, Activity, LineChart, Layers3, Users } from "lucide-react";

interface TomRow {
  id: string;
  org_id: string;
  group_id: string | null;
  member_jid: string;
  formality: number;
  verbosity: number;
  sentiment_baseline: number;
  observation_count: number;
  effective_observations: number | null;
  last_intent: string | null;
  updated_at: string;
}

interface AppliedEvt {
  occurred_at: string;
  payload: { reason?: string; override?: string | null; directives?: string };
}

interface GroupMoodRow {
  id: string;
  group_id: string;
  mood_label: string;
  confidence: number;
  valence: number;
  arousal: number;
  window_end: string;
}

interface ContributionRunRow {
  id: string;
  group_id: string | null;
  contribution_score: number | null;
  with_tom_score: number | null;
  without_tom_score: number | null;
  winner: string | null;
  created_at: string;
}

interface CalibrationRow {
  id: string;
  group_id: string | null;
  brier_score: number | null;
  ece: number | null;
  sample_size: number;
  created_at: string;
}

interface PromptTraceRow {
  id: string;
  group_id: string | null;
  composition_latency_ms: number | null;
  final_prompt_excerpt: string | null;
  created_at: string;
}

function pct(v: number) {
  return Math.round(Math.max(0, Math.min(1, v)) * 100);
}

function sentimentColor(s: number) {
  if (s <= -0.4) return "destructive";
  if (s >= 0.4) return "default";
  return "secondary";
}

export default function TomInsights() {
  const { data: orgId } = useCurrentOrgId();
  const { data: models, isLoading } = useQuery({
    queryKey: ["tom-models-top", orgId],
    enabled: !!orgId,
    queryFn: async (): Promise<TomRow[]> => {
      const { data, error } = await supabase
        .from("tom_models" as any)
        .select("id,org_id,group_id,member_jid,formality,verbosity,sentiment_baseline,observation_count,effective_observations,last_intent,updated_at")
        .eq("org_id", orgId)
        .order("observation_count", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as unknown as TomRow[];
    },
    refetchInterval: 60_000,
  });

  const { data: applied } = useQuery({
    queryKey: ["tom-applied-events", orgId],
    enabled: !!orgId,
    queryFn: async (): Promise<AppliedEvt[]> => {
      const { data, error } = await supabase
        .from("cortex_events")
        .select("occurred_at,payload")
        .eq("org_id", orgId)
        .eq("event_type", "tom.signal.applied")
        .order("occurred_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as AppliedEvt[];
    },
    refetchInterval: 30_000,
  });

  const { data: groupMoods } = useQuery({
    queryKey: ["tom-group-moods", orgId],
    enabled: !!orgId,
    queryFn: async (): Promise<GroupMoodRow[]> => {
      const { data, error } = await supabase
        .from("syra_group_mood")
        .select("id,group_id,mood_label,confidence,valence,arousal,window_end")
        .eq("org_id", orgId)
        .order("window_end", { ascending: false })
        .limit(12);
      if (error) throw error;
      return (data ?? []) as GroupMoodRow[];
    },
    refetchInterval: 60_000,
  });

  const { data: contributionRuns } = useQuery({
    queryKey: ["tom-contribution-runs", orgId],
    enabled: !!orgId,
    queryFn: async (): Promise<ContributionRunRow[]> => {
      const { data, error } = await supabase
        .from("tom_contribution_runs")
        .select("id,group_id,contribution_score,with_tom_score,without_tom_score,winner,created_at")
        .eq("org_id", orgId)
        .order("created_at", { ascending: false })
        .limit(12);
      if (error) throw error;
      return (data ?? []) as ContributionRunRow[];
    },
    refetchInterval: 60_000,
  });

  const { data: calibrations } = useQuery({
    queryKey: ["tom-calibrations", orgId],
    enabled: !!orgId,
    queryFn: async (): Promise<CalibrationRow[]> => {
      const { data, error } = await supabase
        .from("tom_calibration")
        .select("id,group_id,brier_score,ece,sample_size,created_at")
        .eq("org_id", orgId)
        .order("created_at", { ascending: false })
        .limit(12);
      if (error) throw error;
      return (data ?? []) as CalibrationRow[];
    },
    refetchInterval: 60_000,
  });

  const { data: promptTraces } = useQuery({
    queryKey: ["prompt-composition-traces-admin", orgId],
    enabled: !!orgId,
    queryFn: async (): Promise<PromptTraceRow[]> => {
      const { data, error } = await supabase
        .from("syra_prompt_composition_traces")
        .select("id,group_id,composition_latency_ms,final_prompt_excerpt,created_at")
        .eq("org_id", orgId)
        .order("created_at", { ascending: false })
        .limit(12);
      if (error) throw error;
      return (data ?? []) as PromptTraceRow[];
    },
    refetchInterval: 60_000,
  });

  const lastApplied = applied?.[0]?.occurred_at;
  const avgContribution = useMemo(() => {
    if (!contributionRuns?.length) return null;
    return contributionRuns.reduce((sum, row) => sum + Number(row.contribution_score ?? 0), 0) / contributionRuns.length;
  }, [contributionRuns]);
  const avgBrier = useMemo(() => {
    if (!calibrations?.length) return null;
    return calibrations.reduce((sum, row) => sum + Number(row.brier_score ?? 0), 0) / calibrations.length;
  }, [calibrations]);
  const avgPromptLatency = useMemo(() => {
    const withLatency = (promptTraces ?? []).filter((row) => typeof row.composition_latency_ms === "number");
    if (!withLatency.length) return null;
    return withLatency.reduce((sum, row) => sum + Number(row.composition_latency_ms ?? 0), 0) / withLatency.length;
  }, [promptTraces]);

  return (
    <div className="space-y-6">
      <Breadcrumb items={[{ label: "Admin", to: "/admin" }, { label: "ToM Insights" }]} />
      <ShadowModeBanner context="ToM v2 está em rollout shadow — números baixos são esperados." />
      <div>
        <h1 className="text-2xl font-bold font-headline flex items-center gap-2">
          <Brain className="w-6 h-6 text-primary" /> Theory of Mind — observabilidade real
        </h1>
        <p className="text-sm text-muted-foreground">
          Modelos, contribuição, calibração, humor de grupo e traces de composição conectados ao runtime atual.
        </p>
        {!orgId ? <p className="text-sm text-muted-foreground mt-2">Nenhuma organização ativa encontrada para carregar os insights.</p> : null}
      </div>

      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Modelos ativos</CardDescription>
            <CardTitle className="text-2xl">{models?.length ?? "—"}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Aplicações ToM</CardDescription>
            <CardTitle className="text-2xl">{applied?.length ?? 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Contribution score</CardDescription>
            <CardTitle className="text-2xl">{avgContribution != null ? avgContribution.toFixed(2) : "—"}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Brier médio</CardDescription>
            <CardTitle className="text-2xl">{avgBrier != null ? avgBrier.toFixed(2) : "—"}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Prompt p/turno</CardDescription>
            <CardTitle className="text-2xl">{avgPromptLatency != null ? `${Math.round(avgPromptLatency)} ms` : "—"}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="w-4 h-4 text-primary" /> Top membros por observações
            </CardTitle>
            <CardDescription className="text-xs">
              Observações efetivas, intenção recente e traços mais estáveis do modelo ToM.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {isLoading && <p className="text-sm text-muted-foreground">Carregando…</p>}
            {!isLoading && (models?.length ?? 0) === 0 && <p className="text-sm text-muted-foreground">Nenhum modelo ainda.</p>}
            {(models ?? []).slice(0, 12).map((m) => (
              <div key={m.id} className="rounded-md border p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0 flex-wrap">
                    <span className="font-mono text-xs truncate">{m.member_jid}</span>
                    <Badge variant="outline" className="text-[10px]">{m.observation_count} obs</Badge>
                    <Badge variant="outline" className="text-[10px]">eff {Number(m.effective_observations ?? 0).toFixed(1)}</Badge>
                    <Badge variant={sentimentColor(m.sentiment_baseline) as "default" | "secondary" | "destructive"} className="text-[10px]">
                      sent {m.sentiment_baseline.toFixed(2)}
                    </Badge>
                    {m.last_intent ? <Badge variant="secondary" className="text-[10px]">{m.last_intent}</Badge> : null}
                  </div>
                  <span className="text-[10px] text-muted-foreground">{new Date(m.updated_at).toLocaleDateString("pt-BR")}</span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="mb-1 flex justify-between"><span className="text-muted-foreground">Formalidade</span><span className="font-mono">{pct(m.formality)}%</span></div>
                    <Progress value={pct(m.formality)} />
                  </div>
                  <div>
                    <div className="mb-1 flex justify-between"><span className="text-muted-foreground">Verbosidade</span><span className="font-mono">{pct(m.verbosity)}%</span></div>
                    <Progress value={pct(m.verbosity)} />
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4 text-primary" /> Group mood</CardTitle>
              <CardDescription className="text-xs">Últimos humores inferidos por grupo.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {(groupMoods ?? []).slice(0, 6).map((mood) => (
                <div key={mood.id} className="rounded-md border p-3 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-xs">{mood.group_id}</span>
                    <Badge variant="secondary">{mood.mood_label}</Badge>
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground">
                    conf. {Number(mood.confidence ?? 0).toFixed(2)} · valência {Number(mood.valence ?? 0).toFixed(2)} · ativação {Number(mood.arousal ?? 0).toFixed(2)}
                  </div>
                </div>
              ))}
              {(groupMoods?.length ?? 0) === 0 && <p className="text-sm text-muted-foreground">Sem group mood recente.</p>}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><LineChart className="w-4 h-4 text-primary" /> Contribution + calibration</CardTitle>
              <CardDescription className="text-xs">Shadow A/B do ToM e estabilidade calibrada.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {(contributionRuns ?? []).slice(0, 4).map((run) => (
                <div key={run.id} className="rounded-md border p-3 text-xs">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-muted-foreground">{new Date(run.created_at).toLocaleString("pt-BR")}</span>
                    <Badge variant={Number(run.contribution_score ?? 0) > 0.2 ? "default" : "secondary"}>{Number(run.contribution_score ?? 0).toFixed(2)}</Badge>
                  </div>
                  <div className="mt-2 text-muted-foreground">with ToM {Number(run.with_tom_score ?? 0).toFixed(2)} · without {Number(run.without_tom_score ?? 0).toFixed(2)} · winner {run.winner ?? "—"}</div>
                </div>
              ))}
              {(calibrations ?? []).slice(0, 4).map((run) => (
                <div key={run.id} className="rounded-md border p-3 text-xs">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-muted-foreground">{new Date(run.created_at).toLocaleString("pt-BR")}</span>
                    <Badge variant={Number(run.brier_score ?? 1) <= 0.2 ? "default" : "destructive"}>Brier {Number(run.brier_score ?? 0).toFixed(2)}</Badge>
                  </div>
                  <div className="mt-2 text-muted-foreground">ECE {Number(run.ece ?? 0).toFixed(2)} · n={run.sample_size}</div>
                </div>
              ))}
              {(contributionRuns?.length ?? 0) === 0 && (calibrations?.length ?? 0) === 0 && <p className="text-sm text-muted-foreground">Sem runs recentes.</p>}
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Últimas aplicações em runtime</CardTitle>
            <CardDescription className="text-xs">Turnos onde o ToM modulou intent ou estilo.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {(applied ?? []).slice(0, 10).map((e, i) => (
              <div key={i} className="space-y-1 rounded border p-2 text-xs">
                <div className="flex justify-between gap-2">
                  <span className="font-mono text-muted-foreground">{new Date(e.occurred_at).toLocaleString("pt-BR")}</span>
                  {e.payload.override ? <Badge variant="secondary" className="text-[10px]">override→{e.payload.override}</Badge> : null}
                </div>
                <div className="text-muted-foreground"><strong>razão:</strong> {e.payload.reason ?? "—"}</div>
                {e.payload.directives ? <div className="text-muted-foreground"><strong>diretivas:</strong> {e.payload.directives}</div> : null}
              </div>
            ))}
            {(applied?.length ?? 0) === 0 && <p className="text-sm text-muted-foreground">Nenhuma aplicação registrada ainda.</p>}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Layers3 className="w-4 h-4 text-primary" /> Prompt composition traces</CardTitle>
            <CardDescription className="text-xs">Últimas composições auditáveis do runtime central.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {(promptTraces ?? []).slice(0, 8).map((trace) => (
              <div key={trace.id} className="rounded border p-3 text-xs">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-muted-foreground">{new Date(trace.created_at).toLocaleString("pt-BR")}</span>
                  <Badge variant="outline">{trace.composition_latency_ms != null ? `${Math.round(trace.composition_latency_ms)} ms` : "sem latência"}</Badge>
                </div>
                {trace.group_id ? <div className="mt-2 text-muted-foreground">grupo {trace.group_id}</div> : null}
                <p className="mt-2 line-clamp-3 text-muted-foreground">{trace.final_prompt_excerpt ?? "sem excerpt"}</p>
              </div>
            ))}
            {(promptTraces?.length ?? 0) === 0 && <p className="text-sm text-muted-foreground">Sem traces recentes.</p>}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardDescription>Última aplicação</CardDescription>
          <CardTitle className="text-sm">{lastApplied ? new Date(lastApplied).toLocaleString("pt-BR") : "—"}</CardTitle>
        </CardHeader>
      </Card>
    </div>
  );
}
